/**
 * unifiedTimer.js
 *
 * Pure model of the ONE timer vocabulary every timer surface speaks (header pill, workday sheet, Time Tracker screen,
 * briefing hero, home-screen widgets). Two kinds of clock exist and used to be controlled by separate, unequal code:
 *
 * - `task`    -> a project / work-order time tracking (`/widgets/timer/*` facade): start / pause / break / resume / stop,
 * - `workday` -> the clock-in / clock-out workday ("Munkanap", `/workday/*`): start / pause (break) / resume / stop.
 *
 * This module has no React, network or native imports so it is unit-testable: it turns either clock into one
 * {@link TimerDescriptor}, decides which verbs a surface may offer, and computes the OPTIMISTIC next workday state for a
 * verb (the context applies it immediately and rolls back to the previous state when the server refuses).
 *
 * @module helpers/unifiedTimer
 */

import {isPausedState, liveBreakSec, liveElapsedSec} from './workdayRules';
import {resolveTrackerElapsedSeconds, resolveTrackerLabels, resolveTrackerState} from './timeTrackerFormat';

/** The two kinds of clock. */
export const TIMER_KINDS = Object.freeze({TASK: 'task', WORKDAY: 'workday'});

/** Verbs a timer surface can fire. */
export const TIMER_VERBS = Object.freeze(['start', 'pause', 'resume', 'stop']);

/**
 * @typedef {object} TimerDescriptor
 * @property {'task'|'workday'} kind
 * @property {'running'|'paused'|'break'} state `break` is a TRACKED task break; a workday pause is `paused`.
 * @property {string} label Task headline, or `null` for the workday (the surface localizes "Workday").
 * @property {string|null} eyebrow Project / customer line (task only).
 * @property {number} elapsedSec Worked seconds at `now` (frozen while paused).
 * @property {number} breakSec Break seconds at `now` (workday; task break total when known).
 * @property {string[]} verbs Verbs to show, in order (`pause`|`resume`|`stop`).
 */

/**
 * Verbs offered for a clock state. Stop is ALWAYS offered (also while paused) so a paused clock can be ended.
 * @param {'running'|'paused'|'break'} state clock state.
 * @param {{canPause?: boolean}} [caps] capability flags (task timers can opt out of pause).
 * @returns {string[]} ordered verbs.
 */
export const verbsFor = (state, caps = {}) => {
    if (state === 'paused' || state === 'break') {
        return ['resume', 'stop'];
    }

    return caps.canPause === false ? ['stop'] : ['pause', 'stop'];
};

/**
 * Descriptor of the running (or paused) workday, or null when none runs.
 * @param {object|null} state normalized workday state ({@link module:helpers/workdayApi.normalizeWorkdayState}).
 * @param {number} [now] client epoch ms.
 * @returns {TimerDescriptor|null} descriptor.
 */
export const describeWorkday = (state, now = Date.now()) => {
    if (!state?.running) {
        return null;
    }

    const paused = isPausedState(state);

    return {
        kind: TIMER_KINDS.WORKDAY,
        state: paused ? 'paused' : 'running',
        label: null,
        eyebrow: null,
        elapsedSec: liveElapsedSec(state, now),
        breakSec: liveBreakSec(state, now),
        verbs: verbsFor(paused ? 'paused' : 'running'),
    };
};

/**
 * Descriptor of a task time tracking (facade timer object), or null when idle.
 * @param {object|null} timer widget-facade timer ({@link module:helpers/timeTrackingApi}).
 * @param {number} [now] client epoch ms.
 * @returns {TimerDescriptor|null} descriptor.
 */
export const describeTask = (timer, now = Date.now()) => {
    const state = resolveTrackerState(timer);

    if (state === 'idle') {
        return null;
    }

    const labels = resolveTrackerLabels(timer);

    return {
        kind: TIMER_KINDS.TASK,
        state,
        label: labels.headline || null,
        eyebrow: labels.eyebrow || null,
        elapsedSec: resolveTrackerElapsedSeconds(timer, now),
        breakSec: Math.max(0, Math.floor(Number(timer?.breakSeconds) || 0)),
        verbs: verbsFor(state, {canPause: timer?.canPause}),
    };
};

/**
 * Every active clock, task first (the one a person is "working on"), then the workday.
 * @param {{workday?: object|null, task?: object|null, now?: number}} input sources.
 * @returns {TimerDescriptor[]} 0..2 descriptors.
 */
export const describeTimers = ({workday = null, task = null, now = Date.now()} = {}) => (
    [describeTask(task, now), describeWorkday(workday, now)].filter(Boolean)
);

/**
 * The clock a single-timer surface (widget face, pill) should headline: the task when one tracks, otherwise the workday.
 * @param {TimerDescriptor[]} timers result of {@link describeTimers}.
 * @returns {TimerDescriptor|null} primary clock.
 */
export const primaryTimer = (timers) => (Array.isArray(timers) && timers.length ? timers[0] : null);

/**
 * OPTIMISTIC next workday state for a verb. The state is cloned, never mutated. `fetchedAt`/`serverNow` are re-anchored
 * to `now` so the live-clock maths ({@link liveElapsedSec}) continues from the exact frozen / resumed value.
 *
 * - `pause`  : freezes the worked seconds, opens a break (`paused`, `pausedAt = now`).
 * - `resume` : closes the break into `breakSec`, ticking again from the frozen worked seconds.
 * - `stop`   : the running session ends; its worked seconds move into `today.workedSec`.
 *
 * Verbs that do not apply (pause while paused, resume while running, anything without a running workday) return the
 * SAME state object, so callers can use identity to detect a no-op.
 * @param {object|null} state current normalized workday state.
 * @param {'pause'|'resume'|'stop'} verb verb.
 * @param {number} [now] client epoch ms.
 * @returns {object|null} next state (or the unchanged input).
 */
export const optimisticWorkday = (state, verb, now = Date.now()) => {
    if (!state?.running) {
        return state;
    }

    const paused = isPausedState(state);
    const worked = liveElapsedSec(state, now);
    const breakSec = liveBreakSec(state, now);

    if (verb === 'pause') {
        if (paused) {
            return state;
        }

        return {
            ...state,
            elapsedSec: worked,
            paused: true,
            breakSec,
            fetchedAt: now,
            running: {...state.running, paused: true, pausedAt: new Date(now).toISOString(), breakSec},
        };
    }

    if (verb === 'resume') {
        if (!paused) {
            return state;
        }

        return {
            ...state,
            elapsedSec: worked,
            paused: false,
            breakSec,
            fetchedAt: now,
            running: {...state.running, paused: false, pausedAt: null, breakSec},
        };
    }

    if (verb === 'stop') {
        return {
            ...state,
            running: null,
            paused: false,
            elapsedSec: 0,
            breakSec: 0,
            fetchedAt: now,
            today: {
                ...state.today,
                workedSec: Math.max(Number(state.today?.workedSec) || 0, worked),
                sessions: (state.today?.sessions || []).map((s) => (
                    s.id === state.running.id
                        ? {...s, status: 'ended', endedAt: new Date(now).toISOString(), paused: false, pausedAt: null, breakSec}
                        : s
                )),
            },
        };
    }

    return state;
};

/**
 * Tiny mutation tracker that makes optimistic updates safe against late reads: every mutation bumps `epoch`, and a
 * read that STARTED before the latest mutation must be discarded (it describes the pre-mutation world).
 * @returns {{begin: () => number, isStale: (startedAtEpoch: number) => boolean, current: () => number}} tracker.
 */
export const createMutationEpoch = () => {
    let epoch = 0;

    return {
        begin: () => {
            epoch += 1;

            return epoch;
        },
        isStale: (startedAtEpoch) => startedAtEpoch !== epoch,
        current: () => epoch,
    };
};

export default {
    TIMER_KINDS,
    TIMER_VERBS,
    verbsFor,
    describeWorkday,
    describeTask,
    describeTimers,
    primaryTimer,
    optimisticWorkday,
    createMutationEpoch,
};
